import React, { useCallback, useEffect, useRef, useState } from "react";
import { useBnbSelector } from "../../../../redux/store";
import useSpinner, { type GetImageSrcType } from "../../../../../../../common-deprecated/hooks/spin/useSpinner";
import useGetBuildSpinnerFrame, { ImageType } from "../../../../hooks/shared/spin/useGetBuildSpinnerFrame";
import * as InteriorSpinStyles from "./styles/InteriorGalleryStyle";
import { SrOnly } from "../../../../../../../common-deprecated/styles/v2/lexus/utilities/ScreenreaderOnly";
import Icon from "../../../../../../../common-deprecated/styles/v2/lexus/globals/Icon";
import { useWindowDimensions } from "../../../../../../../common-deprecated/hooks";
import { useBreakpoint } from "../../../../../../../common-deprecated/themes/common";
import useSlider from "../../../../../../../common-deprecated/hooks/useSlider";
import { isRTL } from "../../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { trackCustomizeIntGallery } from "../../../../utils/tracking";

type InteriorGalleryPropsType = {
    isFullscreen?: boolean;
};

const InteriorGallery = (props: InteriorGalleryPropsType): JSX.Element => {
    const { isFullscreen } = props;
    const viewPortRef = useRef<HTMLDivElement>(null);
    const sliderRef = useRef<HTMLDivElement>(null);
    const windowWidth = useWindowDimensions().width;
    const [viewPortWidth, setViewPortWidth] = useState<number>(0);
    const [activeImage, setActiveImage] = useState<number>(0);

    const commonSettings = useBnbSelector((state) => state.commonSettings);

    // Slider functionality should only be enabled when the view is mobile.
    // Note that useBreakpoint usually should be avoided, but in this case it seems like the best solution as the server side render should be correct in any use case.
    const isMobile = useBreakpoint("down", "sm");

    const { getImage, cacheKey } = useGetBuildSpinnerFrame(false, ImageType.InteriorGallery);
    const getImageSrc = useCallback(
        (frame: number): GetImageSrcType => ({
            src: getImage(1230, 547, frame),
            srcSet: `${getImage(1230, 547, frame)} 1x, ${getImage(2460, 1094, frame)} 2x`,
            sizes: "",
        }),
        [cacheKey],
    );

    const frameProps = useSpinner(getImageSrc, cacheKey, 0, 3);
    const { imageCache } = frameProps;

    useEffect(() => {
        if (viewPortRef.current && sliderRef.current) {
            const { width } = viewPortRef.current.getBoundingClientRect();
            setViewPortWidth(width);
        }
    }, [viewPortWidth, windowWidth, isFullscreen]);

    const sliderProps = useSlider(
        imageCache.current.length * viewPortWidth,
        viewPortWidth,
        viewPortWidth,
        [],
        isRTL(commonSettings),
    );
    const { startMouseDrag, startTouchDrag, goToPage, sliderX, enableDrag, isScrolling } = sliderProps;
    const { leftArrowClickPage, rightArrowClickPage, leftArrowEnabled, rightArrowEnabled } = sliderProps;

    // Save the active image in state, based on calculating the slider position.
    useEffect(() => {
        const activeIndex =
            sliderX === 0
                ? 0
                : Math.round(
                      Math.abs(
                          sliderX /
                              (((imageCache.current.length || 1) * viewPortWidth) / (imageCache.current.length || 1)),
                      ),
                  );
        if (activeIndex !== activeImage) setActiveImage(activeIndex);
    }, [sliderX, activeImage]);

    // Build images array. Animate and position the images by setting margins and css transitions.
    const images = imageCache.current.map((image) => (
        <div style={{ width: "100%" }}>
            <InteriorSpinStyles.SpinImage
                alt="Interior premium spin"
                key={cacheKey + image.frame}
                {...getImageSrc(image.frame)}
            />
        </div>
    ));

    return (
        <InteriorSpinStyles.SpinInterior hasOutsideBox>
            <InteriorSpinStyles.SpinGallery ref={viewPortRef}>
                <InteriorSpinStyles.SpinImages
                    ref={sliderRef}
                    style={{
                        width: `${imageCache.current.length * 100}%`,
                        transform: `translateX(${sliderX || 0}px)`,
                    }}
                    onTouchStart={enableDrag ? startTouchDrag : undefined}
                    onMouseDown={enableDrag ? startMouseDrag : undefined}
                    onClick={(event) => {
                        if (isScrolling.current) {
                            event.preventDefault();
                            event.stopPropagation();
                        }
                    }}
                    onTransitionEnd={() => {
                        isScrolling.current = false;
                    }}
                >
                    {images}
                </InteriorSpinStyles.SpinImages>
                <InteriorSpinStyles.SpinNavButtons>
                    <InteriorSpinStyles.ButtonLeft
                        type="button"
                        large
                        light
                        hidden={!leftArrowEnabled || isMobile}
                        onClick={() => {
                            trackCustomizeIntGallery();
                            leftArrowClickPage();
                        }}
                    >
                        <Icon variant={isRTL(commonSettings) ? "chevronRight" : "chevronLeft"} />
                    </InteriorSpinStyles.ButtonLeft>
                    <InteriorSpinStyles.ButtonRight
                        type="button"
                        large
                        light
                        hidden={!rightArrowEnabled || isMobile}
                        onClick={() => {
                            trackCustomizeIntGallery();
                            rightArrowClickPage();
                        }}
                    >
                        <Icon variant={isRTL(commonSettings) ? "chevronLeft" : "chevronRight"} />
                    </InteriorSpinStyles.ButtonRight>
                </InteriorSpinStyles.SpinNavButtons>
                <InteriorSpinStyles.SpinNav>
                    {imageCache.current.map((image) => (
                        <InteriorSpinStyles.SpinNavItem
                            isActive={image.frame === activeImage}
                            key={`int-button-${image.frame}`}
                            onClick={() => {
                                goToPage(image.frame);
                            }}
                        >
                            <SrOnly>{image.frame + 1}</SrOnly>
                        </InteriorSpinStyles.SpinNavItem>
                    ))}
                </InteriorSpinStyles.SpinNav>
            </InteriorSpinStyles.SpinGallery>
        </InteriorSpinStyles.SpinInterior>
    );
};

export default InteriorGallery;
